/**
 * Navbar Component
 * Main navigation bar with responsive mobile menu
 * Displays navigation links, authentication controls, and user info
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { SignInButton } from '../auth/SignInButton';
import { CreditPurchaseModal } from '../auth/CreditPurchaseModal';
import { ThemeToggle } from './ThemeToggle';
import { NavigationDrawer } from '../mobile/NavigationDrawer';
/**
 * Navbar component
 * Provides site navigation with responsive hamburger menu for mobile
 * Shows authentication status and user credits when logged in
 *
 * @returns Navigation bar component
 */
export function Navbar() {
    const { isAuthenticated, user, signOut } = useAuthContext();
    const { profile, isLoading: profileLoading } = useUserProfile();
    const { isMobile } = useResponsiveLayout();
    const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
    const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
    // Check if user is admin (you can adjust this logic based on your auth setup)
    const isAdmin = user?.email?.includes('admin') || false;
    // Get credits from profile
    const credits = profile?.currentCredits || 0;
    /**
     * Check if we should reopen credit modal after authentication
     */
    useEffect(() => {
        if (isAuthenticated) {
            const shouldReopen = sessionStorage.getItem('creditModalShouldReopen');
            if (shouldReopen === 'true') {
                setShowCreditPurchaseModal(true);
            }
        }
    }, [isAuthenticated]);
    /**
     * Handle buy credits - always show modal (modal handles sign-in internally)
     */
    const handleBuyCredits = () => {
        setShowCreditPurchaseModal(true);
    };
    return (<>
      <nav className="navbar-container" style={{
            width: '100%',
            background: 'var(--color-surface)',
            color: 'var(--color-fg)',
            fontFamily: 'var(--font-family)',
            fontSize: 'var(--font-size-xs)',
            borderBottom: '4px solid var(--color-accent)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            marginBottom: '1.25rem'
        }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0.5rem 0.5rem' : '1rem' }}>
          <div className="navbar-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem' }}>
            {/* Logo */}
            <Link to="/" className="navbar-logo" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.25rem 0',
            textDecoration: 'none',
            flexShrink: 0
        }}>
              <img src="https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png" alt="8-Bit Composer Logo" style={{ height: '2rem', width: '2rem' }}/>
              <span style={{ color: 'var(--color-accent)', fontSize: 'var(--font-size-sm)' }}>
                8-Bit Composer
              </span>
            </Link>
            
            {/* Hamburger button for mobile */}
            {isMobile && (<button onClick={() => setIsNavDrawerOpen(true)} style={{
                minWidth: '44px',
                minHeight: '44px',
                width: '44px',
                height: '44px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                background: 'transparent',
                border: '2px solid var(--color-border)',
                borderRadius: '4px',
                color: 'var(--color-fg)',
                fontSize: '1.25rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
            }} aria-label="Open navigation menu" title="Open menu">
                <i className="fa-solid fa-bars" aria-hidden="true"></i>
              </button>)}
            
            {/* Navigation links - hidden on mobile */}
            {!isMobile && (<div className="navbar-links" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
                flex: 1,
                justifyContent: 'flex-end'
            }}>

          {isAdmin && (<Link to="/admin" style={{
                    border: '2px solid var(--color-accent)',
                    padding: '0.5rem 1rem',
                    fontSize: '1rem',
                    fontWeight: 'var(--font-weight-normal)',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    color: 'var(--color-fg)',
                    transition: 'all 0.2s'
                }}>
              Admin 📊
            </Link>)}
          <Link to="/" style={{
                color: 'var(--color-fg)',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: 'var(--font-weight-normal)',
                transition: 'color 0.2s'
            }}>
            Composer
          </Link>
          {isAuthenticated && (<>
              <Link to="/profile" style={{
                    color: 'var(--color-fg)',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontWeight: 'var(--font-weight-normal)',
                    transition: 'color 0.2s'
                }}>
                Profile
              </Link>
              <Link to="/my-compositions" style={{
                    color: 'var(--color-fg)',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontWeight: 'var(--font-weight-normal)',
                    transition: 'color 0.2s'
                }}>
                My Compositions
              </Link>
            </>)}
          <Link to="/about" style={{
                color: 'var(--color-fg)',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: 'var(--font-weight-normal)',
                transition: 'color 0.2s'
            }}>
            About
          </Link>
          <Link to="/contact" style={{
                color: 'var(--color-fg)',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: 'var(--font-weight-normal)',
                transition: 'color 0.2s'
            }}>
            Contact
          </Link>

          {/* Divider before auth section */}
          {isAuthenticated && (<div style={{
                    width: '1px',
                    height: '24px',
                    background: 'var(--color-border)',
                    opacity: 0.5
                }}/>)}

          {/* Credits badge when authenticated */}
          {isAuthenticated && !profileLoading && profile && (<div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 1rem',
                    background: 'rgba(255, 193, 7, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                }} onClick={handleBuyCredits} onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 193, 7, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 193, 7, 0.3)';
                }} onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 193, 7, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }} role="button" aria-label="Buy credits" title="Buy more credits">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fa-solid fa-coins" style={{ color: '#ffc107', fontSize: '1.1rem' }}></i>
                <span style={{ color: '#ffc107', fontWeight: '600', fontSize: '1rem' }}>{credits}</span>
              </div>
              <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    paddingLeft: '0.5rem',
                    borderLeft: '1px solid rgba(255, 193, 7, 0.3)'
                }}>
                <i className="fa-solid fa-plus" style={{ color: '#ffc107', fontSize: '0.85rem' }}></i>
                <span style={{ color: '#ffc107', fontSize: '0.75rem', fontWeight: '500', opacity: 0.9 }}>Buy</span>
              </div>
            </div>)}

          <ThemeToggle />
          
          {/* Show Sign In only when NOT authenticated */}
          {!isAuthenticated && <SignInButton />}
          
          {/* Show Sign Out only when authenticated */}
          {isAuthenticated && (<button onClick={signOut} style={{
                    background: 'transparent',
                    border: '2px solid var(--color-border)',
                    color: 'var(--color-fg)',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: 'var(--font-weight-normal)',
                    fontFamily: 'var(--font-family)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textDecoration: 'none'
                }} onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent)';
                    e.currentTarget.style.color = 'var(--color-accent)';
                }} onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.color = 'var(--color-fg)';
                }} aria-label="Sign out">
              Sign Out
            </button>)}
          </div>)}
          </div>
        </div>
      </nav>

      {/* Navigation Drawer for mobile */}
      {isMobile && (<NavigationDrawer isOpen={isNavDrawerOpen} credits={credits} onClose={() => setIsNavDrawerOpen(false)} onBuyCredits={handleBuyCredits}/>)}

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal isOpen={showCreditPurchaseModal} onClose={() => setShowCreditPurchaseModal(false)}/>
    </>);
}
